import React, { useEffect, useMemo, useState } from 'react';

import {
    Box,
    Tab,
    TabPanel,
    Tabs,
    Typography,
} from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { EmptyMessage } from 'common/components';
import ReactHookFormCheckboxButtonGroup from 'common/components/form/ReactHookFormCheckboxButtonGroup';
import {
    DOCUMENTS_TYPE,
    INSURANCE_DOCUMENTS_TYPE,
    INSURANCE_TYPE,
    REQUEST_FORM_TYPE,
} from 'common/constants';

import * as actions from '../actions';
import * as selectors from '../selectors';

const Documents = ({
    control,
    disabled,
    documents,
    fetchDocuments,
    financierType,
    isFetching,
}) => {
    const { t } = useTranslation();
    const [selectedTab, setSelectedTab] = useState('request_form');
    const handleChange = (_, newValue) => setSelectedTab(newValue);

    useEffect(() => {
        fetchDocuments();
    }, []);

    const docs = useMemo(
        () => documents.filter((doc) => doc.type === DOCUMENTS_TYPE),
        [documents],
    );

    const requestDocs = useMemo(() => {
        if (financierType === INSURANCE_TYPE)
            return documents.filter(
                (doc) => doc.type === INSURANCE_DOCUMENTS_TYPE,
            );
        return documents.filter((doc) => doc.type === REQUEST_FORM_TYPE);
    }, [documents]);

    if (isFetching)
        return (
            <EmptyMessage>
                <Typography>{t('Loading')}</Typography>
            </EmptyMessage>
        );

    return (
        <React.Fragment>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                    onChange={handleChange}
                    scrollButtons="auto"
                    value={selectedTab}
                    variant="scrollable"
                >
                    <Tab
                        id="tab-request_form"
                        key="tab-request_form"
                        label={
                            financierType === INSURANCE_TYPE
                                ? t('Insurance application form')
                                : t('Request form')
                        }
                        value="request_form"
                    />
                    <Tab
                        id="tab-documents"
                        key="tab-documents"
                        label={t('Document', { count: 2 })}
                        value="documents"
                    />
                </Tabs>
            </Box>

            <TabPanel
                key="tab-request_form"
                selectedTab={selectedTab}
                value="request_form"
            >
                <ReactHookFormCheckboxButtonGroup
                    control={control}
                    disabled={disabled}
                    items={requestDocs.map((doc) => ({
                        label: doc.name,
                        value: doc.id,
                    }))}
                    name="catalog_documents"
                />
            </TabPanel>

            <TabPanel
                key="tab-documents"
                selectedTab={selectedTab}
                value="documents"
            >
                <ReactHookFormCheckboxButtonGroup
                    control={control}
                    disabled={disabled}
                    items={docs.map((doc) => ({
                        label: doc.name,
                        value: doc.id,
                    }))}
                    name="catalog_documents"
                />
            </TabPanel>
        </React.Fragment>
    );
};

const mapStateToProps = createStructuredSelector({
    documents: selectors.getDocumentsData,
    isFetching: selectors.getDocumentsIsFetching,
});

const mapDispatchToProps = (dispatch) => ({
    fetchDocuments: () => dispatch(actions.fetchDocuments()),
});

Documents.propTypes = {
    control: PropTypes.object,
    disabled: PropTypes.bool,
    documents: PropTypes.array,
    fetchDocuments: PropTypes.func,
    financierType: PropTypes.number,
    isFetching: PropTypes.bool,
};

export default connect(mapStateToProps, mapDispatchToProps)(Documents);
